import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

/**
 * Estilos alineados a ann `tooltip.styled.js` + `colors.js`:
 * - Fondo `surface.neutral.darker` (#3A3A3A), texto `text.negative` (#FFFFFF)
 * - radius-s 4px, padding 2× / 3×, tipografía 14px / regular / Inter
 * - Sombra 0 2px 10px rgba(0,0,0,0.2)
 * Sin Base UI: React + portal + posicionamiento manual + Tailwind.
 */

function ArrowSvg() {
  return (
    <svg width="16" height="8" viewBox="0 0 16 8" fill="none" aria-hidden>
      <path d="M0 0L8 8L16 0H0Z" fill="currentColor" />
    </svg>
  );
}

/**
 * @param {object} props
 * @param {import('react').ReactNode} props.children — trigger (hover/focus target)
 * @param {import('react').ReactNode} props.content — panel body: string or any JSX (icons, lists, headings). Style the inner layout with Tailwind on your root node; `children` is always the trigger only.
 * @param {boolean} [props.arrow=true]
 * @param {string} [props.className]
 * @param {string} [props.id]
 * @param {number} [props.zIndex=1000]
 * @param {'start'|'center'|'end'} [props.align='center'] — horizontal placement vs trigger; when `arrowAlign` is omitted, the arrow follows this (start/end/center under the panel).
 * @param {'top'|'bottom'} [props.position='top']
 * @param {'default'|'compact'} [props.size='default'] — `compact`: smaller padding and `text-xs`; default `maxWidth` becomes tighter unless `maxWidth` is set.
 * @param {'start'|'center'|'end'} [props.arrowAlign] — arrow under the panel; default: same side as `align` (`start`→left, `end`→right, `center`→middle).
 * @param {number} [props.delay=300]
 * @param {number} [props.closeDelay=300]
 * @param {'none'|'dissolve'} [props.openStyle='none']
 * @param {string} [props.maxWidth] — default `360px` (default size) or `min(260px, calc(100vw - 24px))` (compact)
 * @param {string} [props.triggerClassName] — wrapper around children (hover target). Default shrinks to content; use e.g. `flex w-full min-w-0` for full-width triggers.
 * @param {boolean} [props.disabled=false] — when true, hover/focus does not open the panel.
 */
const Tooltip = ({
  children,
  content,
  arrow = true,
  className = '',
  id: idProp,
  zIndex = 1000,
  align = 'center',
  position = 'top',
  size = 'default',
  arrowAlign: arrowAlignProp,
  delay = 300,
  closeDelay = 300,
  openStyle = 'none',
  maxWidth,
  triggerClassName = 'inline-flex w-fit cursor-default',
  disabled = false,
}) => {
  const reactId = useId();
  const tooltipId = idProp || `tooltip-${reactId.replace(/:/g, '')}`;
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const popupRef = useRef(null);
  const openTimerRef = useRef(null);
  const closeTimerRef = useRef(null);

  const clearOpenTimer = useCallback(() => {
    if (openTimerRef.current) {
      clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
  }, []);

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const isCompact = size === 'compact';
  const resolvedMaxWidth =
    maxWidth ??
    (isCompact ? 'min(260px, calc(100vw - 24px))' : '360px');
  const resolvedArrowAlignProp =
    arrowAlignProp === 'start' || arrowAlignProp === 'end' || arrowAlignProp === 'center'
      ? arrowAlignProp
      : null;
  const effectiveArrowAlign =
    resolvedArrowAlignProp ??
    (align === 'start' ? 'start' : align === 'end' ? 'end' : 'center');
  const colItemsClass =
    effectiveArrowAlign === 'start'
      ? 'items-start'
      : effectiveArrowAlign === 'end'
        ? 'items-end'
        : 'items-center';
  const arrowPadClass =
    effectiveArrowAlign === 'start'
      ? isCompact
        ? 'pl-2'
        : 'pl-3'
      : effectiveArrowAlign === 'end'
        ? isCompact
          ? 'pr-2'
          : 'pr-3'
        : '';
  const arrowJustifyClass =
    effectiveArrowAlign === 'start'
      ? 'justify-start'
      : effectiveArrowAlign === 'end'
        ? 'justify-end'
        : 'justify-center';
  const bodySizeClass = isCompact
    ? 'px-2 py-1.5 text-xs leading-snug'
    : 'px-3 py-2 text-sm leading-normal';

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    const popup = popupRef.current;
    if (!trigger || !popup) return;
    const tr = trigger.getBoundingClientRect();
    const pr = popup.getBoundingClientRect();
    if (pr.width === 0 || pr.height === 0) return;

    const gap = 5;
    let top =
      position === 'top' ? tr.top - pr.height - gap : tr.bottom + gap;
    let left;
    if (align === 'center') {
      left = tr.left + tr.width / 2 - pr.width / 2;
    } else if (align === 'start') {
      left = tr.left;
    } else {
      left = tr.right - pr.width;
    }

    const pad = 8;
    left = Math.min(
      Math.max(left, pad),
      Math.max(pad, window.innerWidth - pr.width - pad),
    );
    top = Math.min(
      Math.max(top, pad),
      Math.max(pad, window.innerHeight - pr.height - pad),
    );

    setCoords({ top, left });
  }, [align, position]);

  useLayoutEffect(() => {
    if (!open) return;
    updatePosition();
    const raf = requestAnimationFrame(() => updatePosition());
    return () => cancelAnimationFrame(raf);
  }, [open, content, updatePosition, arrow, resolvedMaxWidth, position, align, size, effectiveArrowAlign]);

  useEffect(() => {
    if (!open) return;
    const ro = new ResizeObserver(() => updatePosition());
    if (triggerRef.current) ro.observe(triggerRef.current);
    if (popupRef.current) ro.observe(popupRef.current);
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      ro.disconnect();
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [open, updatePosition]);

  const scheduleOpen = useCallback(() => {
    if (disabled) return;
    clearCloseTimer();
    clearOpenTimer();
    openTimerRef.current = window.setTimeout(() => setOpen(true), delay);
  }, [delay, disabled, clearCloseTimer, clearOpenTimer]);

  const scheduleClose = useCallback(() => {
    clearOpenTimer();
    closeTimerRef.current = window.setTimeout(() => setOpen(false), closeDelay);
  }, [closeDelay, clearOpenTimer]);

  const cancelClose = useCallback(() => {
    clearCloseTimer();
  }, [clearCloseTimer]);

  useEffect(() => {
    if (disabled) {
      clearOpenTimer();
      setOpen(false);
      return;
    }

    const trigger = triggerRef.current;
    if (!trigger) return;

    const isHovered = trigger.matches(':hover');
    const isFocused = trigger.contains(document.activeElement);
    if (isHovered || isFocused) {
      scheduleOpen();
    }
  }, [disabled, clearOpenTimer, scheduleOpen]);

  useEffect(
    () => () => {
      clearOpenTimer();
      clearCloseTimer();
    },
    [clearOpenTimer, clearCloseTimer],
  );

  const animClass =
    openStyle === 'dissolve' ? 'animate-tooltip-dissolve' : 'animate-tooltip-fade';

  const portal =
    typeof document !== 'undefined' && open
      ? createPortal(
          <div
            ref={popupRef}
            id={tooltipId}
            role="tooltip"
            className={['pointer-events-auto w-max', animClass].filter(Boolean).join(' ')}
            style={{
              position: 'fixed',
              top: coords.top,
              left: coords.left,
              zIndex,
            }}
            onMouseEnter={cancelClose}
            onMouseLeave={scheduleClose}
          >
            <div className={['inline-flex w-max flex-col', colItemsClass].join(' ')}>
              {position === 'bottom' && arrow && (
                <div
                  className={[
                    'flex w-full shrink-0 text-[#3A3A3A] rotate-180',
                    arrowJustifyClass,
                    arrowPadClass,
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  <ArrowSvg />
                </div>
              )}
              <div
                className={[
                  'min-w-0 break-words rounded bg-[#3A3A3A] font-sans font-normal text-white shadow-[0_2px_10px_rgba(0,0,0,0.2)]',
                  bodySizeClass,
                  className,
                ]
                  .filter(Boolean)
                  .join(' ')}
                style={{ maxWidth: resolvedMaxWidth }}
              >
                {content}
              </div>
              {position === 'top' && arrow && (
                <div
                  className={[
                    'flex w-full shrink-0 text-[#3A3A3A]',
                    arrowJustifyClass,
                    arrowPadClass,
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  <ArrowSvg />
                </div>
              )}
            </div>
          </div>,
          document.body,
        )
      : null;

  return (
    <>
      <div
        ref={triggerRef}
        className={triggerClassName}
        aria-describedby={open ? tooltipId : undefined}
        onMouseEnter={scheduleOpen}
        onMouseLeave={scheduleClose}
        onFocusCapture={scheduleOpen}
        onBlurCapture={scheduleClose}
      >
        {children}
      </div>
      {portal}
    </>
  );
};

export default Tooltip;
